import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Eye, 
  Trash2, 
  ShoppingBag, 
  Search, 
  Calendar, 
  DollarSign, 
  Truck,
  Filter,
  Download,
  TrendingDown,
  Package
} from 'lucide-react';
import { purchasesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  ModernCard,
  ModernButton,
  ModernTable,
  ModernBadge,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';

const Purchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { hasAnyRole } = useAuth();

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const response = await purchasesAPI.getPurchases();
      setPurchases(response.data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  const filteredPurchases = purchases.filter(purchase =>
    String(purchase.supplier_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(purchase.employee_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(purchase.store_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(purchase.id || '').includes(searchTerm)
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Handle delete purchase
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet achat ?')) return;
    try {
      await purchasesAPI.deletePurchase(id);
      toast.success('Achat supprimé');
      fetchPurchases();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle view details
  const handleViewDetails = (purchase) => {
    // For now, just show an alert with purchase details
    alert(`Détails de l'achat #${purchase.id}\nDate: ${formatDate(purchase.purchase_date)}\nFournisseur: ${purchase.supplier_name}\nEmployé: ${purchase.employee_name}\nMagasin: ${purchase.store_name}\nTotal: ${formatCurrency(purchase.total)}`);
  };

  // Handle new purchase
  const handleNewPurchase = () => {
    // For now, just show an alert
    alert('Fonctionnalité de création d\'achat à implémenter');
  };

  const tableColumns = [
    { 
      key: 'id', 
      label: 'Achat ID',
      render: (value) => (
        <span className="font-mono text-sm font-semibold text-primary-600">#{value}</span>
      )
    },
    { 
      key: 'purchase_date', 
      label: 'Date',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    { 
      key: 'supplier_name', 
      label: 'Fournisseur',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-warning-100 rounded-full flex items-center justify-center mr-3">
            <span className="text-sm font-medium text-warning-700">
              {value?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
          <span className="font-medium text-neutral-900">{value || 'Inconnu'}</span>
        </div>
      )
    },
    { 
      key: 'employee_name', 
      label: 'Employé',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
            <span className="text-sm font-medium text-primary-700">
              {value?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
          <span className="font-medium text-neutral-900">{value || 'Inconnu'}</span>
        </div>
      )
    },
    { 
      key: 'store_name', 
      label: 'Magasin',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-success-100 rounded-lg flex items-center justify-center mr-2">
            <span className="text-xs font-medium text-success-700">S</span>
          </div>
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    { 
      key: 'total', 
      label: 'Total',
      render: (value) => (
        <span className="font-semibold text-danger-600">
          {formatCurrency(value)}
        </span>
      )
    },
    { 
      key: 'actions', 
      label: 'Actions',
      render: (value, purchase) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Eye className="w-4 h-4" />}
            onClick={() => handleViewDetails(purchase)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(purchase.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const totalSpent = purchases.reduce((sum, purchase) => sum + (purchase.total || 0), 0);
  const thisMonthPurchases = purchases.filter(purchase => {
    const purchaseDate = new Date(purchase.purchase_date);
    const now = new Date();
    return purchaseDate.getMonth() === now.getMonth() && 
           purchaseDate.getFullYear() === now.getFullYear();
  });
  const uniqueSuppliers = new Set(purchases.map(p => p.supplier_name).filter(Boolean)).size;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Achats</h1>
          <p className="text-neutral-600">Gérez vos achats et fournisseurs</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des achats..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          {hasAnyRole(['admin', 'manager']) && (
            <ModernButton
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={handleNewPurchase}
            >
              Nouvel achat
            </ModernButton>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Achats</p>
              <p className="text-2xl font-bold text-neutral-900">{purchases.length}</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Dépensé</p>
              <p className="text-2xl font-bold text-danger-600">
                {formatCurrency(totalSpent)}
              </p>
            </div>
            <div className="w-12 h-12 bg-danger-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Ce Mois</p>
              <p className="text-2xl font-bold text-primary-600">
                {thisMonthPurchases.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Fournisseurs</p>
              <p className="text-2xl font-bold text-success-600">{uniqueSuppliers}</p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Purchases Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Achats récents</h3>
            <p className="text-sm text-neutral-600">{filteredPurchases.length} achats trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredPurchases}
          className="w-full"
        />
      </ModernCard>
    </div>
  );
};

export default Purchases; 